<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Being a teacher is considered to be one of the most fulfilling and rewarding careers out
        there, as you’re able to make a big difference to your students’ lives.
      </p>
      <p class="mb-5">In this experience, we will go through:</p>
      <ul class="default-list mb-5">
        <li>The different kinds of secondary school teachers</li>
        <li>What qualifications you need to get started as a teacher</li>
        <li>Some of the theories and tools teachers use to create great lessons for students</li>
        <li>Some exercises you can do to get a taste of what it’s like to be a teacher.</li>
      </ul>
      <p>Let’s dive in!</p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">Let’s start your virtual job experience in High School Education!</h4>
      <p class="mb-5">
        You’ve most likely experienced what a secondary school teacher does as a student, but what
        does the job look like from the other side of the desk?
      </p>
      <p class="mb-5">
        On its face, secondary school teaching is about instructing and guiding students to help
        them learn about one or more subjects based on a curriculum. But in the process of doing so,
        they are also encouraging students’ development of their social, emotional, intellectual and
        physical skills to help get them ready for their adult lives.
      </p>
      <p class="mb-5">
        Typically, teachers are experts in their field that are very passionate about their
        subjects, and enjoy the practice of helping students develop their skills and enthusiasm in
        the subject as well.
      </p>

      <h4 class="mb-5">What are the different kinds of teachers?</h4>
      <p class="mb-5">
        Teachers usually work within certain areas of study (sometimes called ‘faculties’), which
        are collections of subjects that use similar skills or content. These can include:
      </p>
      <vs-collapse accordion>
        <vs-collapse-item>
          <div slot="header">Mathematics</div>
          <p class="mb-5">
            Mathematics isn’t just a subject - it’s a gateway to other areas of study such as
            science, technology and engineering. Maths teachers provide students with knowledge and
            practice in areas such as number theory, algebra, measurement and geometry, as well as
            statistics and probability.
          </p>
          <p class="mb-5">
            Skills that are important for a maths teacher include having an eye for problem solving,
            relating class content to real-world examples, and being able to explain complex ideas
            in easy-to-understand terms.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Science</div>
          <p class="mb-5">
            Science teachers encourage students to approach the world around them with curiosity and
            wonder. It’s a very diverse field of study, ranging from more (written word)-heavy
            subjects such as biology or environmental sciences, to more mathematics-heavy subjects
            such as chemistry or physics. It’s also a faculty which often blends theory and
            practice, including experiments and practical classes.
          </p>
          <p class="mb-5">
            Great science teachers are able to encourage scientific thinking, curiosity and a desire
            to ask questions about life and the universe.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Human Society and its Environment</div>
          <p class="mb-5">
            Human Society and its Environment (HSIE) includes Aboriginal studies, business studies,
            commerce, economics, geography, history, legal studies, society and culture, and
            religion. It is one of the broadest faculties available, and each subject requires quite
            a lot of content knowledge.
          </p>
          <p class="mb-5">
            HSIE more than any other faculty delves into how we as people interact with each other,
            our governments, different nations and cultures, as well as our natural environment.
            Great HSIE teachers are able to keep their subjects relevant by empowering students to
            critically analyse how these different ways of looking at the world interact with their
            daily lives.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Technology and Applied Sciences</div>
          <p class="mb-5">
            Technology and Applied Sciences is a thrilling subject that demands teachers stay
            up-to-date in their area of expertise. TAS teachers incorporate new theories,
            technologies and techniques into their lessons to help students equip themselves with
            the practical knowledge to live and work in the contemporary world.
          </p>
          <p class="mb-5">
            Subjects such as agriculture, industrial technology, design and technology, engineering
            studies, food technology, and software design don’t just build students’ knowledge in
            theory, but give them skills they can directly apply to their everyday lives.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">English</div>
          <p class="mb-5">
            In many ways, English is the study of communication and literacy. Literacy isn’t just
            about reading Shakespeare or Tim Winton - it’s about being able to derive meaning from
            all different kinds of texts, and to develop and communicate your own ideas and
            reflections about those texts and the wider world.
          </p>
          <p class="mb-5">
            It is also uniquely positioned as a subject that is taught at all ability levels from
            when a student enrols in Kindergarten through to their final year at school. You could
            be teaching English Studies or English as an Additional Language or Dialect (EAL/D) all
            the way through to English Extension 2.
          </p>
          <p class="mb-5">
            Excellent English teachers encourage students to engage meaningfully with texts,
            relating the communication skills they learn in the classroom with how they interact
            with others and their environment at large.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Creative and Performing Arts</div>
          <p class="mb-5">
            If you have a creative streak and are passionate about one or more of the arts, the
            Creative and Performing Arts faculty can be a great option. The faculty includes dance,
            drama, music, photographic and digital media, and visual arts.
          </p>
          <p class="mb-5">
            Typically these classes will be a blend of theoretical study, combined with practical
            elements including major works or performances.
          </p>
          <p class="mb-5">
            As well as having a specialisation in your chosen artistic discipline, a great Arts
            teacher is able to inspire students to experiment, take risks and improve their craft
            while they engage with the subject. In senior years, helping students organise and
            execute large-scale projects such as major works is an important skill to have.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Languages</div>
          <p class="mb-5">
            Languages are a really exciting way for students to broaden their cultural horizons by
            being able to communicate with a diverse range of people. They can also be incredibly
            useful in building a deeper understanding of students’ first language through comparing
            and contrasting spelling, grammar and syntax.
          </p>
          <p class="mb-5">
            Languages can be a challenging subject for students, so having a natural enthusiasm for
            languages, being skilled in the languages you teach as well as being familiar with the
            culture surrounding users of that language will give you an advantage in your practice.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Personal Development, Health and Physical Education</div>
          <p class="mb-5">
            Personal Development, Health and Physical Education represents one of the most
            universally applicable faculty areas. We all experience and interact with the topics
            covered in these subjects in some form or another.
          </p>
          <p class="mb-5">
            As topics such as health are so important for students to take with them to their lives
            outside of the classroom, having teachers who are genuinely passionate about empowering
            students to learn about and take ownership of their wellbeing is a very important
            attribute.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Special and Inclusive Education</div>
          <p class="mb-5">
            Special and Inclusive Education presents a strong opportunity to make an incredible
            impact on students who have additional learning and support needs in a classroom
            setting.
          </p>
          <p class="mb-5">
            It requires an additional level of flexibility, empathy and understanding in order to
            adapt lesson plans and outcomes to suit students with a diverse range of abilities. More
            than any other area of specialisation, Special and Inclusive Education teachers will
            communicate with stakeholders such as other teachers, support staff, and health and
            well-being professionals to make sure their students are able to engage with learning
            resources as best as possible.
          </p>
        </vs-collapse-item>
      </vs-collapse>
    </vx-card>

    <vx-card class="mt-base mb-5">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <h4 class="mb-5">What do you need to become a secondary school teacher?</h4>
          <p class="mb-5">
            To become a secondary teacher, you will need to complete at least four years of tertiary
            study at a recognised university or higher education institution. Your studies will need
            to include an accredited teacher education program and adequate study of the subject(s)
            you would like to teach. Appropriate teacher education courses include:
          </p>
          <ul class="default-list">
            <li>a Bachelor of Education (Secondary)</li>
            <li>
              a combined or double degree, such as a Bachelor of Science/Bachelor of Education
              (Secondary)
            </li>
            <li>
              an undergraduate degree and an accredited graduate-entry teaching degree, such as a
              Bachelor of Teaching (Secondary) or a Master of Teaching (Secondary).
            </li>
          </ul>

          <p class="mb-5">
            Source: <a href="https://education.nsw.gov.au">education.nsw.gov.au</a>
          </p>

          <h4 class="mb-5">What tasks and responsibilities do secondary school teachers have?</h4>
          <ul class="default-list">
            <li>
              Preparing and teaching a range of lessons to different ages and abilities (up to six
              different Years levels from 7 to 12)
            </li>
            <li>
              Using a variety of methods, including formal lessons, discussions, practical
              activities, experiments, assessments and excursions to engage students and enhance
              their learning experiences
            </li>
            <li>Preparing, setting and marking assessments, including providing feedback</li>
            <li>Assessing, evaluating and recording student development</li>
            <li>Teaching additional classes when other teachers are absent</li>
            <li>Encouraging students to maintain effective study habits</li>
            <li>Implementing classroom management practices</li>
            <li>Discussing students' development with parents</li>
            <li>Supervising students outside the classroom (e.g. during breaks)</li>
            <li>Undertaking professional development</li>
            <li>Attending staff meetings</li>
            <li>
              Assisting with organising sporting activities, excursions and other extracurricular
              activities
            </li>
            <li>Networking with other teachers, particularly those who teach the same subjects</li>
          </ul>
          <p class="mb-5">
            Source: <a href="https://education.nsw.gov.au">education.nsw.gov.au</a>
          </p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/teacher/jo1.png"
            class="mx-auto img-responsive"
            alt="Secondary School Teachers job outlook: $1,914 weekly pay, moderate future growth, 148,300 workers, very high skill level, 76% full-time, 46 hours average full-time week, average age 43, 62% female."
          />
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Highlights of Becoming a Secondary School Teacher</h4>
      <ol class="numbered-list">
        <li>
          Contribute To Your Areas of Interest By Inspiring Others<br /><br />
          Chances are that if you become a secondary school teacher, it is because you love your
          chosen subject of specialisation. You may have even been inspired by your own teacher as
          you were going through high school. Teaching gives you the opportunity to inspire your
          students to continue to learn that subject beyond your class - in their everyday lives, in
          a dedicated career or even as a teacher themselves.
        </li>

        <li>
          Work in a Rewarding Profession<br /><br />
          Educating others and giving them the tools to be successful in their lives is an
          incredibly rewarding experience. Secondary school teachers are uniquely positioned to be
          role models and influential people in a young person’s life during a crucial phase where
          they are forming the identity that they will take with them into adulthood. A great
          teacher can have an incredible impact on a student’s welfare and overall attitude towards
          education and learning during this time.
        </li>

        <li>
          Lifestyle-friendly time off<br /><br />
          As a teacher, your time in class mirrors your students’. That doesn’t necessarily mean
          that you won’t be doing any work at all during the term breaks - they can be a great time
          to do administrative work and lesson planning, but you do get to spend more time at home
          with family or friends than in many other professions.
        </li>
      </ol>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      image: require('@/assets/images/experiences/experience-one.png'),
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      showVideoPopup: false,
      videoTriggerImg: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
    };
  },
  provide: function () {
    return {
      openPopup: this.openPopup,
      closePopup: this.closePopup,
    };
  },
  methods: {
    openPopup() {
      this.showVideoPopup = true;
    },
    closePopup() {
      this.showVideoPopup = false;
    },
  },
};
</script>
